import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Sun, DollarSign, MapPin, GraduationCap, Megaphone, TrendingUp, Award,
  Phone, Mail, ArrowLeft, CheckCircle, Menu, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { vantaggiFranchising } from '../mockData';

const iconMap = {
  DollarSign, MapPin, GraduationCap, Megaphone, TrendingUp, Award
};

const FranchisingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    telefono: '',
    email: '',
    citta: '',
    esperienza: '',
    messaggio: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.telefono.trim().length < 8) {
      toast.error("Inserisci un numero di telefono valido (minimo 8 cifre)");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const botFieldValue = new FormData(e.target).get('bot-field') || '';
      
      const leadData = {
        nome: formData.nome,
        telefono: formData.telefono,
        email: formData.email,
        citta: formData.citta,
        esperienza: formData.esperienza || '',
        messaggio: formData.messaggio || '',
        bot_field: botFieldValue // Honeypot field
      };
      
      const response = await fetch(`${API_URL}/api/franchising-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let message = 'Si è verificato un errore. Riprova più tardi.';
        if (Array.isArray(errorData.detail)) {
          // Pydantic 422 validation errors -> friendly Italian message
          const fieldLabels = {
            nome: 'Nome e Cognome', telefono: 'Telefono', email: 'Email',
            citta: 'Città / Zona di Interesse', esperienza: 'Esperienza Pregressa',
            messaggio: 'Messaggio'
          };
          const fields = errorData.detail
            .map(err => fieldLabels[err.loc && err.loc[1]])
            .filter(Boolean);
          message = fields.length
            ? `Verifica i seguenti campi: ${[...new Set(fields)].join(', ')}`
            : 'Alcuni campi non sono validi. Controlla e riprova.';
        } else if (typeof errorData.detail === 'string' && errorData.detail) {
          message = errorData.detail;
        }
        throw new Error(message);
      }
      
      toast.success("Richiesta inviata! Ti contatteremo per approfondire l'opportunità.");
      setFormData({ nome: '', telefono: '', email: '', citta: '', esperienza: '', messaggio: '' });
    } catch (error) {
      console.error('Franchising form submission error:', error);
      toast.error(error.message || "Si è verificato un errore. Riprova più tardi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Navigation */}
      <nav className="nav-header">
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] flex items-center justify-center">
              <Sun className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>Alt Bollette</span>
          </Link>
        </div>
        
        <div className="hidden md:flex items-center gap-1">
          <Link to="/" className="nav-link flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Torna alla Home
          </Link>
        </div>

        <div className="hidden md:block">
          <a href="#form-franchising">
            <Button className="btn-primary">
              Richiedi Informazioni
            </Button>
          </a>
        </div>

        <button 
          className="md:hidden p-2"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X /> : <Menu />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden fixed top-[5rem] left-0 right-0 bg-white border-b border-[var(--border-light)] z-40 p-4 space-y-2">
          <Link to="/" className="block nav-link" onClick={() => setIsMenuOpen(false)}>
            <ArrowLeft className="w-4 h-4 inline mr-1" />
            Torna alla Home
          </Link>
          <a href="#form-franchising">
            <Button className="btn-primary w-full" onClick={() => setIsMenuOpen(false)}>
              Richiedi Informazioni
            </Button>
          </a>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Diventa Partner Alt Bollette
          </h1>
          <p className="hero-subtitle">
            Apri il tuo franchising in esclusiva nella tua zona a costo zero e sfrutta l'onda delle energie rinnovabili
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <a href="#form-franchising">
              <Button className="btn-primary">
                Scopri i Vantaggi
              </Button>
            </a>
            <a href="#vantaggi">
              <Button className="btn-secondary">
                Come Funziona
              </Button>
            </a>
          </div>
        </div>
        <div className="mt-12 max-w-4xl mx-auto px-4">
          <img 
            src="https://images.pexels.com/photos/9875685/pexels-photo-9875685.jpeg" 
            alt="Vista aerea di case con impianti solari"
            className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
          />
        </div>
      </section>

      {/* Opportunità */}
      <section className="pad-2xl" style={{ background: 'var(--bg-section)' }}>
        <div className="container max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">
              L'Opportunità del Momento
            </h2>
            <p className="body-large max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
              Il mercato delle energie rinnovabili sta esplodendo. La domanda di soluzioni per azzerare le bollette è in crescita esponenziale. È il momento perfetto per entrare in un settore in forte espansione con un modello di business già testato e vincente.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="product-card text-center">
              <CardContent className="pt-6">
                <div className="mb-4">
                  <span className="text-5xl font-bold" style={{ color: 'var(--accent-text)' }}>+300%</span>
                </div>
                <h3 className="heading-3 mb-2">Crescita Mercato</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Il settore delle rinnovabili cresce a ritmi record anno dopo anno
                </p>
              </CardContent>
            </Card>

            <Card className="product-card text-center">
              <CardContent className="pt-6">
                <div className="mb-4">
                  <span className="text-5xl font-bold" style={{ color: 'var(--accent-text)' }}>€0</span>
                </div>
                <h3 className="heading-3 mb-2">Investimento Iniziale</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Avvia la tua attività senza costi di ingresso. Zero rischio finanziario
                </p>
              </CardContent>
            </Card>

            <Card className="product-card text-center">
              <CardContent className="pt-6">
                <div className="mb-4">
                  <span className="text-5xl font-bold" style={{ color: 'var(--accent-text)' }}>100%</span>
                </div>
                <h3 className="heading-3 mb-2">Esclusiva Zona</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Territorio protetto senza concorrenza interna al network
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Vantaggi */}
      <section id="vantaggi" className="pad-2xl">
        <div className="container max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">I Vantaggi del Franchising Alt Bollette</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Un modello di business completo, supporto continuo e un mercato in espansione
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vantaggiFranchising.map((vantaggio, index) => {
              const IconComponent = iconMap[vantaggio.icon];
              return (
                <Card key={index} className="product-card">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-full mb-3 flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                      <IconComponent className="w-6 h-6" style={{ color: 'var(--accent-text)' }} />
                    </div>
                    <CardTitle className="product-card-title">{vantaggio.titolo}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="product-card-description">{vantaggio.descrizione}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cosa Offriamo */}
      <section className="pad-2xl" style={{ background: 'var(--bg-section)' }}>
        <div className="container max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Cosa Offriamo ai Nostri Partner</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                  <CheckCircle className="w-6 h-6" style={{ color: 'var(--accent-text)' }} />
                </div>
              </div>
              <div>
                <h3 className="heading-3 mb-2">Formazione Completa</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Formazione tecnica e commerciale approfondita per operare con successo nel settore delle rinnovabili
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                  <CheckCircle className="w-6 h-6" style={{ color: 'var(--accent-text)' }} />
                </div>
              </div>
              <div>
                <h3 className="heading-3 mb-2">Supporto Marketing</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Materiali promozionali, campagne digitali e strategie per generare lead costanti nella tua area
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                  <CheckCircle className="w-6 h-6" style={{ color: 'var(--accent-text)' }} />
                </div>
              </div>
              <div>
                <h3 className="heading-3 mb-2">Sistema Consolidato</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Processi operativi testati e ottimizzati, software gestionale e supporto amministrativo
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                  <CheckCircle className="w-6 h-6" style={{ color: 'var(--accent-text)' }} />
                </div>
              </div>
              <div>
                <h3 className="heading-3 mb-2">Prodotti di Qualità</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Accesso a tecnologie di ultima generazione con fornitori selezionati e prezzi competitivi
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                  <CheckCircle className="w-6 h-6" style={{ color: 'var(--accent-text)' }} />
                </div>
              </div>
              <div>
                <h3 className="heading-3 mb-2">Assistenza Continua</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Team dedicato sempre disponibile per supportarti in ogni fase del progetto
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                  <CheckCircle className="w-6 h-6" style={{ color: 'var(--accent-text)' }} />
                </div>
              </div>
              <div>
                <h3 className="heading-3 mb-2">Brand Affermato</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Sfrutta la reputazione e la riconoscibilità del marchio Alt Bollette
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chi Cerchiamo */}
      <section className="pad-2xl">
        <div className="container max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Il Profilo Ideale</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Cerchiamo imprenditori motivati, con spirito commerciale e voglia di crescere in un settore in forte espansione
            </p>
          </div>

          <Card className="product-card">
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div>
                  <h3 className="heading-3 mb-2">✓ Ambizione e Determinazione</h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                    Voglia di costruire un business di successo nel settore delle energie rinnovabili
                  </p>
                </div>
                <div>
                  <h3 className="heading-3 mb-2">✓ Orientamento Commerciale</h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                    Capacità relazionali e attitudine alla vendita (non è necessaria esperienza pregressa)
                  </p>
                </div>
                <div>
                  <h3 className="heading-3 mb-2">✓ Radicamento Territoriale</h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                    Conoscenza del territorio e capacità di sviluppare una rete locale
                  </p>
                </div>
                <div>
                  <h3 className="heading-3 mb-2">✓ Visione Imprenditoriale</h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                    Voglia di investire tempo ed energia per costruire un'attività redditizia e duratura
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Form Franchising */}
      <section id="form-franchising" className="pad-2xl" style={{ background: 'var(--gradient-hero)' }}>
        <div className="container max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="heading-2 mb-4">Richiedi Informazioni sul Franchising</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Compila il form per ricevere il dossier completo e scoprire come diventare partner Alt Bollette
            </p>
          </div>

          <Card className="product-card">
            <CardContent className="pt-6">
              <form 
                onSubmit={handleSubmit} 
                className="space-y-4"
                data-testid="franchising-form"
              >
                {/* Honeypot field for spam protection */}
                <p hidden>
                  <label>
                    Don't fill this out: <input name="bot-field" />
                  </label>
                </p>

                <div>
                  <Label htmlFor="nome">Nome e Cognome *</Label>
                  <Input
                    id="nome"
                    name="nome"
                    type="text"
                    required
                    data-testid="franchising-nome-input"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Mario Rossi"
                  />
                </div>

                <div>
                  <Label htmlFor="telefono">Telefono *</Label>
                  <Input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    required
                    data-testid="franchising-telefono-input"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    placeholder="+39 333 1234567"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    data-testid="franchising-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="mario.rossi@email.it"
                  />
                </div>

                <div>
                  <Label htmlFor="citta">Città / Zona di Interesse *</Label>
                  <Input
                    id="citta"
                    name="citta"
                    type="text"
                    required
                    data-testid="franchising-citta-input"
                    value={formData.citta}
                    onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                    placeholder="Milano"
                  />
                </div>

                <div>
                  <Label htmlFor="esperienza">Esperienza Pregressa</Label>
                  <Input
                    id="esperienza"
                    name="esperienza"
                    type="text"
                    value={formData.esperienza}
                    onChange={(e) => setFormData({ ...formData, esperienza: e.target.value })}
                    placeholder="Es: vendita, edilizia, imprenditoria..."
                  />
                </div>

                <div>
                  <Label htmlFor="messaggio">Messaggio (Opzionale)</Label>
                  <Textarea
                    id="messaggio"
                    name="messaggio"
                    value={formData.messaggio}
                    onChange={(e) => setFormData({ ...formData, messaggio: e.target.value })}
                    placeholder="Raccontaci qualqualcosa in più su di te e le tue motivazioni..."
                    rows={4}
                  />
                </div>

                <Button type="submit" disabled={isSubmitting} className="btn-primary w-full" data-testid="franchising-submit-button">
                  {isSubmitting ? 'Invio in corso...' : 'Richiedi il Dossier Completo'}
                </Button>

                <div className="text-center space-y-1 pt-2">
                  <p className="body-small font-semibold" style={{ color: 'var(--accent-text)' }}>✓ Nessun impegno</p>
                  <p className="body-small font-semibold" style={{ color: 'var(--accent-text)' }}>✓ Informazioni gratuite</p>
                  <p className="body-small font-semibold" style={{ color: 'var(--accent-text)' }}>✓ Risposta entro 24 ore</p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="pad-xl" style={{ background: 'rgb(0, 55, 32)', color: 'white' }}>
        <div className="container max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] flex items-center justify-center">
                  <Sun className="w-6 h-6 text-white" />
                </div>
                <span className="font-bold text-xl">Alt Bollette</span>
              </div>
              <p className="body-medium opacity-90">
                Soluzioni energetiche complete per azzerare le tue bollette
              </p>
            </div>

            <div>
              <h3 className="heading-3 mb-4 text-white">Contatti Franchising</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  <a href="tel:+393501483135" className="body-small hover:underline">+39 350 148 3135</a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  <a href="mailto:franchising@altbollette.info" className="body-small hover:underline">franchising@altbollette.info</a>
                </div>
              </div>
            </div>

            <div>
              <h3 className="heading-3 mb-4 text-white">Link Utili</h3>
              <div className="space-y-2">
                <Link to="/" className="block body-medium hover:underline">
                  Homepage
                </Link>
                <a href="#vantaggi" className="block body-medium hover:underline">Vantaggi</a>
                <a href="#form-franchising" className="block body-medium hover:underline">Richiedi Info</a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-6 text-center">
            <p className="body-small opacity-75">
              © 2024 Alt Bollette. Tutti i diritti riservati. | Privacy Policy | Cookie Policy
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FranchisingPage;
